'use client';

import { useLocale, useTranslations } from 'next-intl';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { Button } from '@/shared/ui/shadcn/button';
import { formatDate } from '@/shared/lib/utils';
import {
  useDesignLineVersions,
  useRestoreDesignLineVersion,
  type DesignLineHistoryKind,
} from '../hooks/use-design-line';

interface DesignLineHistorySheetProps {
  kind: DesignLineHistoryKind;
  lineId: string | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface HistoryRow {
  id: string;
  note: string | null | undefined;
  createdAt: string;
  changes: string[];
  isCurrent: boolean;
}

/**
 * "Change history" of a design line (estimate or blueprint), opened from the
 * concept page "⋯" menu. Newest first; the current version is marked, past
 * ones offer "Restore this version" (a new current version, server-side).
 * Row title is the API `revision_note`, the second line is the date plus the
 * API `design_changes` joined with "; ".
 */
export function DesignLineHistorySheet({
  kind,
  lineId,
  open,
  onOpenChange,
}: DesignLineHistorySheetProps) {
  const t = useTranslations('common.designLine');
  const locale = useLocale();
  const { data, isLoading } = useDesignLineVersions(kind, lineId, open);
  const restore = useRestoreDesignLineVersion(kind);

  // Albums carry no change list; estimates do.
  const rows: HistoryRow[] = (data ?? []).map((version) => ({
    id: version.id,
    note: version.revision_note,
    createdAt: version.created_at,
    changes: 'design_changes' in version ? version.design_changes : [],
    isCurrent: !version.is_read_only,
  }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{t('history')}</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <ul className="divide-y divide-border rounded-xl bg-card">
            {rows.map((row) => (
              <li key={row.id} className="flex items-center gap-3 px-4 py-3">
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="text-settings-row-title">{row.note}</span>
                  <span className="text-card-meta text-muted-foreground">
                    {[formatDate(row.createdAt, locale), ...row.changes].join('; ')}
                  </span>
                </div>
                {row.isCurrent ? (
                  <span className="text-card-meta text-muted-foreground flex-shrink-0">
                    {t('currentVersion')}
                  </span>
                ) : (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="flex-shrink-0"
                    disabled={restore.isPending || !lineId}
                    onClick={() =>
                      lineId &&
                      restore.mutate(
                        { lineId, versionId: row.id },
                        { onSuccess: () => onOpenChange(false) }
                      )
                    }
                  >
                    {t('restoreVersion')}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
